import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ClipboardCheck,
  LayoutDashboard,
  Loader2,
  Search,
  Stethoscope,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { recipeApi as doctorRecipeApi } from '../../api/doctor/recipe/apis';
import { recipeApi as adminRecipeApi } from '../../api/admin/RecipeApi';
import { teamApi } from '../../api/teamApi';
import type { Recipe } from '../../api/admin/RecipeApi';
import { getRecipeIdentifier, normalizeRecipeId } from '../../utils/recipeId';

type SuggestionKind =
  | 'pages'
  | 'my'
  | 'review'
  | 'approval'
  | 'team'
  | 'catalog';

type Suggestion = {
  id: string;
  kind: SuggestionKind;
  title: string;
  subtitle?: string;
  href: string;
  disabled?: boolean;
};

type MenuItemDef = {
  id: string;
  label: string;
  path: string;
  keywords: string[];
  section: string;
  /** If true, only shown when doctor profile is approved */
  requiresApproved?: boolean;
  /** If true, only shown when user can approve */
  requiresCanApprove?: boolean;
};

const RESULT_LIMIT = 6;

/** Mirrors doctor sidebar + common aliases so menu search works globally */
const DOCTOR_MENU_ITEMS: MenuItemDef[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/doctor/dashboard',
    section: 'Manage remedies',
    keywords: ['dashboard', 'home', 'overview', 'main'],
  },
  {
    id: 'my-remedies',
    label: 'My remedies',
    path: '/doctor/remedy',
    section: 'Manage remedies',
    keywords: [
      'my remedies',
      'remedy',
      'remedies',
      'recipes',
      'recipe',
      'create',
      'draft',
    ],
  },
  {
    id: 'my-team',
    label: 'My team',
    path: '/doctor/team',
    section: 'Manage remedies',
    requiresApproved: true,
    keywords: [
      'my team',
      'team',
      'invite',
      'member',
      'members',
      'collaboration',
    ],
  },
  {
    id: 'review-queue',
    label: 'Review remedies',
    path: '/doctor/review-queue',
    section: 'Manage remedies',
    requiresApproved: true,
    keywords: [
      'review remedies',
      'review',
      'reviews',
      'review queue',
      'doctor review',
    ],
  },
  {
    id: 'approver-queue',
    label: 'Approval queue',
    path: '/doctor/approver-queue',
    section: 'Manage remedies',
    requiresCanApprove: true,
    keywords: [
      'approval queue',
      'approver',
      'approve',
      'pending approval',
      'approvals',
    ],
  },
  {
    id: 'appointments',
    label: 'Appointments',
    path: '/doctor/appointment',
    section: 'Appointments',
    keywords: [
      'appointments',
      'appointment',
      'schedule',
      'calendar',
      'booking',
    ],
  },
  {
    id: 'profile',
    label: 'My profile',
    path: '/doctor/profile',
    section: 'Account',
    keywords: [
      'my profile',
      'profile',
      'doctor profile',
      'credentials',
      'license',
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/doctor/setting',
    section: 'Account',
    keywords: ['settings', 'setting', 'preferences', 'account', 'config'],
  },
];

type DoctorGlobalSearchModalProps = {
  open: boolean;
  onClose: () => void;
};

const KIND_META: Record<SuggestionKind, { label: string; icon: LucideIcon }> = {
  pages: { label: 'Pages & menu', icon: LayoutDashboard },
  my: { label: 'My remedies', icon: BookOpen },
  review: { label: 'Review queue', icon: Stethoscope },
  approval: { label: 'Approval queue', icon: ClipboardCheck },
  team: { label: 'Team', icon: Users },
  catalog: { label: 'Published remedies', icon: BookOpen },
};

function matchesQuery(haystack: string, q: string): boolean {
  const h = haystack.toLowerCase();
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  return terms.every((t) => h.includes(t));
}

const DoctorGlobalSearchModal = ({
  open,
  onClose,
}: DoctorGlobalSearchModalProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = Boolean(user?.can_approve || user?.canApprove);
  const isApproved = user?.doctorProfile?.status === 'approved';
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataSuggestions, setDataSuggestions] = useState<Suggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebouncedQuery('');
    setDataSuggestions([]);
    setActiveIndex(0);
    setError('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(t);
  }, [query]);

  const pageSuggestions = useMemo((): Suggestion[] => {
    if (!debouncedQuery) return [];
    return DOCTOR_MENU_ITEMS.filter((item) => {
      if (item.requiresApproved && !isApproved) return false;
      if (item.requiresCanApprove && !canApprove) return false;
      const hay = [item.label, item.section, item.path, ...item.keywords].join(
        ' ',
      );
      return matchesQuery(hay, debouncedQuery);
    }).map((item) => ({
      id: `page-${item.id}`,
      kind: 'pages' as const,
      title: item.label,
      subtitle: item.section,
      href: item.path,
    }));
  }, [debouncedQuery, isApproved, canApprove]);

  useEffect(() => {
    if (!open) return;
    if (!debouncedQuery) {
      setDataSuggestions([]);
      setLoading(false);
      setError('');
      return;
    }

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const requests: Array<Promise<unknown>> = [
          doctorRecipeApi.getAllRecipes({
            page: 1,
            limit: RESULT_LIMIT,
            search: debouncedQuery,
          }),
          doctorRecipeApi.getParentRecipeOptions({
            page: 1,
            limit: RESULT_LIMIT,
            search: debouncedQuery,
          }),
        ];

        if (isApproved) {
          requests.push(
            adminRecipeApi.getDoctorReviewQueue({
              page: 1,
              limit: RESULT_LIMIT,
              search: debouncedQuery,
              review_status: 'pending',
            }),
            teamApi.getIncomingInvites(),
            teamApi.getOutgoingInvites(),
            teamApi.listMembers().catch(() => ({
              success: false,
              data: undefined,
            })),
          );
        }

        if (canApprove) {
          requests.push(
            adminRecipeApi.getApproverQueue({
              page: 1,
              limit: RESULT_LIMIT,
              search: debouncedQuery,
            }),
          );
        }

        const results = await Promise.all(requests);
        if (cancelled) return;

        let idx = 0;
        const myRes = results[idx++] as Awaited<
          ReturnType<typeof doctorRecipeApi.getAllRecipes>
        >;
        const catalogRes = results[idx++] as Awaited<
          ReturnType<typeof doctorRecipeApi.getParentRecipeOptions>
        >;

        let reviewRes: Awaited<
          ReturnType<typeof adminRecipeApi.getDoctorReviewQueue>
        > | null = null;
        let incomingRes: Awaited<
          ReturnType<typeof teamApi.getIncomingInvites>
        > | null = null;
        let outgoingRes: Awaited<
          ReturnType<typeof teamApi.getOutgoingInvites>
        > | null = null;
        let membersRes: Awaited<ReturnType<typeof teamApi.listMembers>> | null =
          null;

        if (isApproved) {
          reviewRes = results[idx++] as Awaited<
            ReturnType<typeof adminRecipeApi.getDoctorReviewQueue>
          >;
          incomingRes = results[idx++] as Awaited<
            ReturnType<typeof teamApi.getIncomingInvites>
          >;
          outgoingRes = results[idx++] as Awaited<
            ReturnType<typeof teamApi.getOutgoingInvites>
          >;
          membersRes = results[idx++] as Awaited<
            ReturnType<typeof teamApi.listMembers>
          >;
        }

        const approvalRes = canApprove
          ? (results[idx++] as Awaited<
              ReturnType<typeof adminRecipeApi.getApproverQueue>
            >)
          : null;

        const next: Suggestion[] = [];
        const q = debouncedQuery.toLowerCase();

        if (myRes.success && myRes.data?.recipes) {
          for (const r of myRes.data.recipes) {
            const id = String(r.uuid ?? r.id ?? '');
            if (!id) continue;
            next.push({
              id: `my-${id}`,
              kind: 'my',
              title: String(r.title || r.internal_name || 'Untitled remedy'),
              subtitle: String(r.status || '').replace(/_/g, ' ') || undefined,
              href: `/doctor/remedy/edit/${id}`,
            });
          }
        }

        if (reviewRes?.success && reviewRes.data) {
          const rows = Array.isArray(reviewRes.data.data)
            ? (reviewRes.data.data as Recipe[])
            : [];
          for (const r of rows) {
            const id = getRecipeIdentifier(r);
            next.push({
              id: `review-${id}`,
              kind: 'review',
              title: r.title || 'Untitled remedy',
              subtitle: [
                r.status?.replace(/_/g, ' '),
                r.author?.name ? `by ${r.author.name}` : '',
              ]
                .filter(Boolean)
                .join(' · '),
              href: `/doctor/reviews/${normalizeRecipeId(id)}`,
            });
          }
        }

        if (approvalRes?.success && approvalRes.data) {
          const rows = Array.isArray(approvalRes.data.data)
            ? (approvalRes.data.data as Recipe[])
            : [];
          for (const r of rows) {
            const id = getRecipeIdentifier(r);
            next.push({
              id: `approval-${id}`,
              kind: 'approval',
              title: r.title || 'Untitled remedy',
              subtitle: [
                r.status?.replace(/_/g, ' '),
                r.author?.name ? `by ${r.author.name}` : '',
              ]
                .filter(Boolean)
                .join(' · '),
              href: '/doctor/approver-queue',
            });
          }
        }

        if (catalogRes.success && catalogRes.data?.recipes) {
          for (const r of catalogRes.data.recipes) {
            const id = String(r.id ?? '');
            if (!id) continue;
            if (
              next.some((s) => s.kind === 'my' && s.href.endsWith(`/${id}`))
            ) {
              continue;
            }
            next.push({
              id: `catalog-${id}`,
              kind: 'catalog',
              title: String(r.title || r.name || 'Untitled remedy'),
              subtitle: r.authorName
                ? `Published · ${r.authorName}`
                : 'Published remedy',
              href: `/doctor/remedy/view/${id}`,
            });
          }
        }

        const teamHits: Suggestion[] = [];
        if (incomingRes?.success && incomingRes.data?.invitations) {
          for (const inv of incomingRes.data.invitations) {
            const hay = `${inv.teamName || ''} ${inv.ownerName || ''} ${inv.email || ''}`;
            if (!matchesQuery(hay, q)) continue;
            teamHits.push({
              id: `team-in-${inv.id}`,
              kind: 'team',
              title: inv.teamName || 'Team invite',
              subtitle: `Incoming invite${inv.ownerName ? ` from ${inv.ownerName}` : ''}`,
              href: '/doctor/team',
            });
          }
        }
        if (outgoingRes?.success && outgoingRes.data?.invitations) {
          for (const inv of outgoingRes.data.invitations) {
            const hay = `${inv.email || ''} ${inv.inviteeAccountTypeLabel || ''} ${inv.status || ''}`;
            if (!matchesQuery(hay, q)) continue;
            teamHits.push({
              id: `team-out-${inv.id}`,
              kind: 'team',
              title: inv.email || 'Team invite',
              subtitle: `Outgoing · ${inv.status || 'pending'}`,
              href: '/doctor/team',
            });
          }
        }
        if (membersRes?.success && membersRes.data?.members) {
          for (const m of membersRes.data.members) {
            const hay = `${m.name || ''} ${m.email || ''} ${m.accountTypeLabel || ''}`;
            if (!matchesQuery(hay, q)) continue;
            teamHits.push({
              id: `team-member-${m.userId}`,
              kind: 'team',
              title: m.name || m.email || 'Team member',
              subtitle:
                `Member · ${m.email || m.accountTypeLabel || ''}`.trim(),
              href: '/doctor/team',
            });
          }
        }
        next.push(...teamHits.slice(0, RESULT_LIMIT));

        setDataSuggestions(next);
        setActiveIndex(0);
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError('Search failed. Please try again.');
          setDataSuggestions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [open, debouncedQuery, canApprove, isApproved]);

  const suggestions = useMemo(
    () => [...pageSuggestions, ...dataSuggestions],
    [pageSuggestions, dataSuggestions],
  );

  const grouped = useMemo(() => {
    const order: SuggestionKind[] = canApprove
      ? ['pages', 'my', 'review', 'approval', 'catalog', 'team']
      : ['pages', 'my', 'review', 'catalog', 'team'];
    return order
      .map((kind) => ({
        kind,
        items: suggestions.filter((s) => s.kind === kind),
      }))
      .filter((g) => g.items.length > 0);
  }, [suggestions, canApprove]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  // Keep active index in range when results change
  useEffect(() => {
    setActiveIndex((i) =>
      flat.length === 0 ? 0 : Math.min(i, flat.length - 1),
    );
  }, [flat.length]);

  const goTo = useCallback(
    (item: Suggestion) => {
      if (item.disabled) return;
      onClose();
      navigate(item.href);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flat[activeIndex];
      if (item) goTo(item);
    }
  };

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[12vh] sm:pt-[15vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        aria-label="Close search"
        onClick={onClose}
      />

      <div className="relative w-full max-w-xl rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden font-body">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, remedies, reviews, team..."
            className="flex-1 min-w-0 bg-transparent text-gray-900 placeholder:text-gray-400 outline-none text-base"
            autoComplete="off"
          />
          {loading ? (
            <Loader2 className="w-4 h-4 text-herbal-green animate-spin shrink-0" />
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}

          {!debouncedQuery && !loading && (
            <div className="px-4 py-6">
              <p className="text-sm text-center text-gray-500 mb-4">
                Search sidebar pages and live data from the server.
              </p>
              <ul className="space-y-1">
                {DOCTOR_MENU_ITEMS.filter((item) => {
                  if (item.requiresApproved && !isApproved) return false;
                  if (item.requiresCanApprove && !canApprove) return false;
                  return true;
                })
                  .slice(0, 6)
                  .map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() =>
                          goTo({
                            id: `page-${item.id}`,
                            kind: 'pages',
                            title: item.label,
                            href: item.path,
                          })
                        }
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
                      >
                        {item.label}
                        <span className="text-xs text-gray-400 ml-2">
                          {item.section}
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {debouncedQuery && !loading && flat.length === 0 && !error && (
            <p className="px-4 py-8 text-sm text-center text-gray-500">
              No suggestions for “{debouncedQuery}”.
            </p>
          )}

          {grouped.map((group) => {
            const MetaIcon = KIND_META[group.kind].icon;
            return (
              <div key={group.kind} className="py-2">
                <p className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <MetaIcon className="w-3.5 h-3.5" />
                  {KIND_META[group.kind].label}
                </p>
                <ul>
                  {group.items.map((item) => {
                    runningIndex += 1;
                    const index = runningIndex;
                    const active = index === activeIndex;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => goTo(item)}
                          className={`w-full text-left px-4 py-2.5 transition-colors ${
                            active
                              ? 'bg-herbal-green/10 text-gray-900'
                              : 'hover:bg-gray-50 text-gray-800'
                          }`}
                        >
                          <div className="text-sm font-medium truncate">
                            {item.title}
                          </div>
                          {item.subtitle && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate">
                              {item.subtitle}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 flex items-center justify-between gap-2">
          <span>↑↓ navigate · Enter open · Esc close</span>
          <span>Pages + live server results</span>
        </div>
      </div>
    </div>
  );
};

export default DoctorGlobalSearchModal;
